import { AlertCircle, Check, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { AnomalyIndicator } from '@/components/AnomalyIndicator';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { Timesheet } from '@/types/timesheet';
import { calculateTimesheetTotal } from '@/utils/ApprovalDashboard/timesheetTotals';

export interface BulkApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTimesheets: Timesheet[];
  selectedWithAnomalies: Timesheet[];
  timesheetAnomalies: Map<number, AnomalyDetectionResult>;
  bulkApprovalNotes: string;
  onBulkApprovalNotesChange: (notes: string) => void;
  isBulkApproving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function BulkApprovalDialog({
  open,
  onOpenChange,
  selectedTimesheets,
  selectedWithAnomalies,
  timesheetAnomalies,
  bulkApprovalNotes,
  onBulkApprovalNotesChange,
  isBulkApproving,
  onCancel,
  onConfirm,
}: BulkApprovalDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <AlertDialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to approve {selectedTimesheets.length} timesheet{selectedTimesheets.length !== 1 ? 's' : ''}. 
            Please review the details below before proceeding.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 my-4 overflow-y-auto min-h-0">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Total Timesheets</div>
              <div className="text-2xl font-bold">{selectedTimesheets.length}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Total Hours</div>
              <div className="text-2xl font-bold">
                {selectedTimesheets.reduce((sum, ts) => sum + calculateTimesheetTotal(ts), 0).toFixed(1)}h
              </div>
            </div>
          </div>

          {/* Anomaly Warning */}
          {selectedWithAnomalies.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  Warning: {selectedWithAnomalies.length} Timesheet{selectedWithAnomalies.length !== 1 ? 's' : ''} with Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  The following timesheets have detected anomalies. Please review them carefully:
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {selectedWithAnomalies.slice(0, 10).map(ts => {
                    const anomalyResult = timesheetAnomalies.get(ts.id);
                    const highSeverityCount = anomalyResult?.anomalies.filter(a => a.severity === 'high').length || 0;

                    return (
                      <div key={ts.id} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                        <span className="font-medium">{ts.employeeName}</span>
                        <div className="flex gap-1">
                          {highSeverityCount > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {highSeverityCount} High
                            </Badge>
                          )}
                          <AnomalyIndicator 
                            anomalyResult={anomalyResult!}
                            size="sm"
                          />
                        </div>
                      </div>
                    );
                  })}
                  {selectedWithAnomalies.length > 10 && (
                    <div className="text-xs text-muted-foreground text-center pt-2">
                      ...and {selectedWithAnomalies.length - 10} more
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Employee List */}
          <div>
            <div className="text-sm font-medium mb-2">Affected Employees:</div>
            <div className="max-h-[150px] overflow-y-auto p-3 bg-muted/30 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                {selectedTimesheets.map(ts => (
                  <div key={ts.id} className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-green-600" />
                    <span>{ts.employeeName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Approval Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Add an approval note (optional)</label>
            <Textarea
              placeholder="e.g. Approved — all project codes verified."
              value={bulkApprovalNotes}
              onChange={(e) => onBulkApprovalNotesChange(e.target.value.slice(0, 500))}
              rows={2}
            />
            <div className="flex justify-end text-xs text-muted-foreground">
              {bulkApprovalNotes.length} / 500
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBulkApproving} onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Keep the dialog open while the batch runs so the progress state is visible;
              // the confirm handler closes it when the batch finishes.
              e.preventDefault();
              onConfirm();
            }}
            disabled={isBulkApproving}
          >
            {isBulkApproving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {isBulkApproving
              ? `Approving ${selectedTimesheets.length} Timesheets…`
              : `Approve All ${selectedTimesheets.length} Timesheets`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
